import { sessionContinue, summarizeData, recommendQuestions } from '@/services';
import type { ChatFlowStrategy, FlowContext } from './types';
import { useChatStore } from '@/store';
import { t } from '@/i18n';

/**
 * 默认流程（极速模式 / 非分析师角色）
 *
 * sessionContinue → 渲染图表结果
 *   → summarizeData（SSE 流式，结果逐步追加到图表下方）
 *   → recommendQuestions（fire-and-forget）
 */
export const defaultFlow: ChatFlowStrategy = {
  async sendMessage(question: string, context: FlowContext) {
    const { currentRole, setStreaming } = useChatStore.getState();
    const { accessToken = '', chatId = '', addMessage, updateMessage, setLoading, setQuickQuestions, signal } =
      context;

    addMessage({
      role: 'user',
      type: 'default',
      bizPayload: {
        role: currentRole.id,
        accessToken,
        chatId,
      },
      content: { text: question },
      status: 'success',
    });

    const aiMsgId = addMessage({
      role: 'assistant',
      type: 'default',
      bizPayload: {
        role: currentRole.id,
        accessToken,
        chatId,
      },
      content: { progressText: '- 正在分析问题...', progressStatus: 'running' },
      status: 'running',
    });

    try {
      setLoading(true);
      setQuickQuestions([]);

      const result = await sessionContinue(accessToken, chatId, question, false, signal);

      if (result.type === 'error' || result.success === false) {
        updateMessage(aiMsgId, {
          content: { text: result.message || t('chat.answerFailed'), progressStatus: 'failed' },
          status: 'error',
        });
        setLoading(false);
        return;
      }
      updateMessage(aiMsgId, {
        content: {
          text: result.content ?? '',
          progressStatus: 'succeeded',
          chart: result.chart
            ? {
              type: result.chart.type,
              title: result.chart.title,
              dimensions: result.chart.dimensions,
              metrics: result.chart.metrics,
              series: result.chart.series,
              data: result.data,
            }
            : undefined,
          tableData: result.data,
          sql: result.sql,
        },
        status: 'success',
        recordId: result.record_id,
        bizPayload: { chatRecordId: result.record_id ?? '' },
      });

      if (result.record_id && result.type === 'data_chart') {
        // 流式总结：使用 Promise 包装确保所有分支都能正确清理状态
        try {
          setLoading(true);
          setStreaming(true);

          const summaryEventSource = await summarizeData(chatId, result.record_id, accessToken, signal);

          // 使用 Promise 包装 SSE 流式处理，确保所有分支都能正确清理状态
          await new Promise<void>((resolve) => {
            const summaryProgressText = '- 正在生成总结...';
            let finished = false;
            let summaryContent = '';

            const finish = (status: 'success' | 'error' | 'cancelled') => {
              if (finished) return;
              finished = true;
              setStreaming(false);
              setLoading(false);
              summaryEventSource.close();

              const updates: any = { status };
              if (status === 'error') {
                updates.content = {
                  progressText: summaryProgressText,
                  text: summaryContent || t('chat.answerFailed'),
                  progressStatus: 'failed',
                };
              } else if (status === 'success') {
                updates.content = {
                  progressText: summaryProgressText,
                  text: summaryContent,
                  progressStatus: 'succeeded',
                };
                // 总结完成后再拉推荐问题
                if (result.record_id) {
                  recommendQuestions(result.record_id, accessToken, signal)
                    .then((qs) => setQuickQuestions(qs))
                    .catch(() => { });
                }
              } else if (status === 'cancelled') {
                updates.content = { progressStatus: 'cancelled' };
              }
              // cancelled 时保留已有内容，仅改状态
              updateMessage(summaryMsgId, updates);
              resolve();
            };

            // 添加总结消息
            const summaryMsgId = addMessage({
              role: 'assistant',
              type: 'default',
              bizPayload: {
                role: currentRole.id,
                accessToken,
                chatId,
              },
              content: { progressText: summaryProgressText, progressStatus: 'running' },
              status: 'running',
            });

            // signal abort 时主动触发取消处理，确保状态清理
            signal?.addEventListener('abort', () => finish('cancelled'), { once: true });

            summaryEventSource.onmessage = (event: MessageEvent) => {
              try {
                const data = JSON.parse(event.data);
                if (data.done) {
                  finish('success');
                } else {
                  summaryContent += data.text || '';
                  updateMessage(summaryMsgId, {
                    content: { progressText: summaryProgressText, text: summaryContent, progressStatus: 'running' },
                    status: 'streaming',
                  });
                }
              } catch (e) {
                console.error('Error parsing summary stream:', e);
              }
            };

            summaryEventSource.onerror = (e: Event) => {
              if (signal?.aborted) {
                finish('cancelled');
                return;
              }
              console.error('Summary EventSource error:', e);
              finish('error');
            };
          });
        } catch {
          if (signal?.aborted) {
            // 取消时只改状态，不清空内容
            setStreaming(false);
            setLoading(false);
            return;
          }

          // 总结失败不影响主流程，静默跳过
          setStreaming(false);
          setLoading(false);
        }
      }
    } catch {
      if (signal?.aborted) {
        // 取消时只改状态，不清空内容
        updateMessage(aiMsgId, {
          content: { progressStatus: 'cancelled' },
          status: 'cancelled',
        });
        setStreaming(false);
        setLoading(false);
        return;
      }

      updateMessage(aiMsgId, {
        content: { text: t('chat.answerFailed'), progressStatus: 'failed' },
        status: 'error',
      });
      setStreaming(false);
      setLoading(false);
    } finally {

    }
  },
};
